import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Loader2, Mail, Settings, ShieldAlert, X } from 'lucide-react'
import { useActingAs, clearActingAs } from '@/state/actingAs'
import { useOrganizationWorkflows } from '@/features/admin/workflowsQueries'
import { useTriggerFetchEmails } from '@/features/admin/workflowSetupQueries'
import { toast } from '@/hooks/use-toast'
import type { ApiRequestError } from '@/services/api'

const BANNER_CONTROL =
  'inline-flex items-center gap-1 rounded border border-amber-950/20 px-2 py-0.5 hover:bg-amber-500/50'

/**
 * Reads the mailbox now, for the workflow whose pages are being looked at.
 *
 * A scheduled workflow has the initiator doing this every minute, so the button
 * would only ever race it. A manual one has nothing to wait for, and until now
 * the only way to make it read its mailbox was to leave the product, go to the
 * admin tool and find the workflow there.
 */
function FetchEmailsButton({
  organizationId,
  workflowId,
}: {
  organizationId: string
  workflowId: string
}) {
  const trigger = useTriggerFetchEmails(organizationId, workflowId)

  async function fetchNow() {
    try {
      await trigger.mutateAsync()
      toast({ title: 'Fetching emails', description: 'The mailbox will be read shortly.' })
    } catch (error) {
      toast({
        variant: 'destructive',
        description: (error as ApiRequestError).message ?? 'Could not fetch emails',
      })
    }
  }

  return (
    <button
      type="button"
      onClick={fetchNow}
      disabled={trigger.isPending}
      className={`${BANNER_CONTROL} disabled:opacity-60`}
    >
      {trigger.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
      Fetch emails
    </button>
  )
}

/**
 * Shown across the whole app whenever admin act-as state is set. Leaving is
 * only via the Exit button — clicking around the app doesn't clear it.
 */
export function ActingAsBanner() {
  const navigate = useNavigate()
  const actingAs = useActingAs()

  // Both product trees mount their pages under a :workflowId, so this is the
  // workflow being looked at.
  //
  // useParams reports the whole matched tree, not just the route this banner
  // renders on, so the admin tool's own workflow pages supply one too. These
  // two controls are shortcuts from the product into admin, and on an admin
  // page they would point at where you already are and repeat a button that
  // page already has, so admin is excluded by path.
  const { workflowId: routeWorkflowId } = useParams<{ workflowId?: string }>()
  const { pathname } = useLocation()
  const workflowId = pathname.startsWith('/admin') ? undefined : routeWorkflowId

  // Only ever asked for on a workflow's own pages, and keyed the same as the
  // admin tool's copy, so it is usually already in the cache by the time an
  // admin has acted their way into a workflow.
  const { data: workflows, isPending } = useOrganizationWorkflows(
    actingAs && workflowId ? actingAs.organizationId : undefined,
  )

  if (!actingAs) return null

  const workflow = workflowId ? workflows?.find((candidate) => candidate.id === workflowId) : undefined

  // A scheduled workflow is already being read every minute, so the button is
  // offered only where it does something: a manual workflow, or one whose start
  // mode could not be established. Hiding a working button is the worse of the
  // two mistakes, and the trigger is harmless to press. While the lookup is in
  // flight neither is known, so nothing is shown and the button does not
  // flicker into place and back out.
  //
  // Unknown is judged on whether the workflow is in hand, not on whether the
  // last request failed: a background refetch that fails over data we already
  // have leaves the start mode just as known as it was.
  const lookupSettled = !isPending
  const startModeUnknown = lookupSettled && workflow === undefined
  const canFetchEmails =
    Boolean(workflowId) && lookupSettled && (workflow?.start_mode === 'manual' || startModeUnknown)

  function exit() {
    const organizationId = actingAs!.organizationId
    clearActingAs()
    navigate(`/admin/workflows?organization=${organizationId}`)
  }

  return (
    <div className="flex items-center justify-between gap-2 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950">
      <span className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4" />
        Acting as {actingAs.organizationName}
      </span>
      <span className="flex items-center gap-2">
        {canFetchEmails && workflowId && (
          <FetchEmailsButton organizationId={actingAs.organizationId} workflowId={workflowId} />
        )}
        {/* Unlike the fetch, this is worth offering whatever the start mode is:
            every workflow has a mailbox, a flow and an ERP connection to look at. */}
        {workflowId && (
          <Link
            to={`/admin/organizations/${actingAs.organizationId}/workflows/${workflowId}/setup`}
            className={BANNER_CONTROL}
          >
            <Settings className="h-3 w-3" />
            Configure
          </Link>
        )}
        <button type="button" onClick={exit} className={BANNER_CONTROL}>
          Exit
          <X className="h-3 w-3" />
        </button>
      </span>
    </div>
  )
}
